import { Controller } from '@hotwired/stimulus';
import { useIdle } from 'stimulus-use';

import consumer from '../../channels/consumer';
import { getCsrfToken } from '../../lib/rs/utils';

export default class extends Controller {
  static values = {
    idleTimeout: Number,
    simulatedProgressDuration: 10000,
    maxSimulatedProgress: { type: Number, default: 99 },
  };

  static targets = ['progressBar', 'form', 'calendlyEmbed'];

  connect() {
    if (this.idleTimeoutValue) {
      useIdle(this, { ms: this.idleTimeoutValue * 1000 });
    }

    document.addEventListener(
      'turbo:before-stream-render',
      this.#injectAuthenticityToken
    );

    window.addEventListener('message', (e) => {
      this.#recordCalendlyEvent(e);
    });
  }

  calendlyEmbedTargetConnected(target) {
    window.Calendly.initInlineWidget({
      url: this.formTarget.dataset.calendlyUrl,
      parentElement: target,
      resize: true,
    });
  }

  progressBarTargetConnected(progressBar) {
    this.#subscribeToDemoFormChannel(progressBar.dataset.demoFormId);
  }

  progressBarTargetDisconnected() {
    this.#unsubscribeFromDemoFormChannel();
  }

  away() {
    window.location.reload();
  }

  scrollNext(event) {
    event.preventDefault();

    const { currentTarget } = event;
    const selector = currentTarget.getAttribute('href');
    const element = document.querySelector(selector);

    element.scrollIntoView({ behavior: 'smooth' });
  }

  disconnect() {
    this.#unsubscribeFromDemoFormChannel();
  }

  #subscribeToDemoFormChannel(demoFormId) {
    if (!demoFormId) {
      return;
    }

    this.channel = consumer.subscriptions.create(
      {
        channel: 'DemoFormsChannel',
        id: demoFormId,
      },
      {
        connected: () => {
          this.#simulateProgress(
            this.maxSimulatedProgressValue,
            this.simulatedProgressDurationValue
          );
        },
        received: (data) => {
          if (data.status === 'posts_created' || data.error) {
            this.#simulateProgress(100, 150);
          }
        },
      }
    );
  }

  #simulateProgress(percentComplete, duration, timingFunction = 'ease-out') {
    this.progressBarTarget.style.transitionProperty = 'width';
    this.progressBarTarget.style.transitionTimingFunction = timingFunction;
    this.progressBarTarget.style.transitionDuration = `${duration}ms`;
    this.progressBarTarget.style.width = `${percentComplete}%`;
  }

  #unsubscribeFromDemoFormChannel() {
    this.channel?.unsubscribe();
  }

  #injectAuthenticityToken(event) {
    const targetForm =
      event.detail.newStream.templateElement.content.querySelector('form');

    if (
      targetForm === null ||
      targetForm.querySelector('input[name="authenticity_token"]') !== null
    )
      return;

    event.preventDefault();

    const metaToken = getCsrfToken();
    const input = document.createElement('input');

    input.name = 'authenticity_token';
    input.type = 'hidden';
    input.value = metaToken;

    targetForm.prepend(input);

    event.detail.newStream.performAction();
  }

  #isRecordableCalendlyEvent(event) {
    const recordableEvents = [
      'calendly.event_type_viewed',
      'calendly.date_and_time_selected',
      'calendly.event_scheduled',
    ];

    return (
      this.#isCalendlyEvent(event) &&
      recordableEvents.includes(event.data.event)
    );
  }

  #isCalendlyEvent(event) {
    return (
      event.origin === 'https://calendly.com' &&
      event.data.event?.indexOf('calendly.') === 0
    );
  }

  #recordCalendlyEvent(event) {
    if (!this.#isRecordableCalendlyEvent(event)) {
      return;
    }

    fetch(this.formTarget.action, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCsrfToken(),
      },
      body: JSON.stringify({
        demo_form: { last_calendly_event: event.data.event },
      }),
    });
  }
}
